/**
 * Formatting helpers used across the app to keep candidate data consistent.
 *
 * Phone numbers:
 *   - displayed and stored as `06 12 34 56 78` (10 digits, 5 groups of 2)
 *   - any input is normalized: strip non-digits, drop leading +33 / 0033 prefix,
 *     keep the trailing 9 digits and prepend "0".
 *
 * Last names:
 *   - always uppercased (French administrative convention).
 *
 * Both helpers tolerate empty / nullish input and return "" in that case.
 */

export function formatPhoneFr(raw) {
  if (!raw) return "";
  let digits = String(raw).replace(/\D/g, "");
  if (!digits) return "";
  // International forms → strip the FR country code before length-truncating so we don't lose digits.
  if (digits.startsWith("0033")) digits = "0" + digits.slice(4);
  else if (digits.startsWith("33") && digits.length > 10) digits = "0" + digits.slice(2);
  // If input starts with "00" (i.e. malformed international) strip the extra leading 0
  if (digits.length > 10 && digits.startsWith("00")) digits = "0" + digits.slice(2);
  // Truncate to 10 digits max (anything beyond is noise)
  digits = digits.slice(0, 10);
  // Group by 2
  return digits.match(/.{1,2}/g)?.join(" ") || digits;
}

/**
 * Return the 10 raw digits of a formatted French phone (or empty string).
 * Useful for validation and length checks.
 */
export function phoneDigitsFr(raw) {
  if (!raw) return "";
  return String(raw).replace(/\D/g, "").slice(0, 10);
}

/**
 * Validate a French phone number.
 * Returns { valid, digits, error } where:
 *   - valid: true if 10 digits + starts with 0[1-9]
 *   - digits: normalized 10-digit string (or partial)
 *   - error: user-facing FR message when invalid; "" when empty or valid
 * An EMPTY value is considered valid (use a separate "required" check upstream).
 */
export function validatePhoneFr(raw) {
  const digits = phoneDigitsFr(raw);
  if (!digits) return { valid: true, digits: "", error: "" };
  if (digits.length < 10) {
    return { valid: false, digits, error: `Le numéro doit contenir 10 chiffres (${digits.length}/10)` };
  }
  if (!/^0[1-9]/.test(digits)) {
    return { valid: false, digits, error: "Numéro invalide (doit commencer par 01–09)" };
  }
  return { valid: true, digits, error: "" };
}

/**
 * Used in onChange handlers so the user sees the formatted value immediately
 * while typing. We re-format on every keystroke.
 */
export function onPhoneInputChange(value, setter) {
  setter(formatPhoneFr(value));
}

export function upperLastName(raw) {
  if (!raw) return "";
  return String(raw).toLocaleUpperCase("fr-FR");
}

/**
 * Format an ISO date string (or Date) as `dd/mm/yyyy`.
 * Returns "" when input is empty/invalid.
 */
export function formatDateFr(input) {
  if (!input) return "";
  try {
    const d = typeof input === "string" ? new Date(input) : input;
    if (Number.isNaN(d.getTime())) return "";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yy = d.getFullYear();
    return `${dd}/${mm}/${yy}`;
  } catch {
    return "";
  }
}

/**
 * Sort candidate-like objects by LASTNAME then firstname (alphabetical).
 */
export function sortByLastName(list) {
  return [...(list || [])].sort((a, b) => {
    const aLast = (a.last_name || a.name || "").toLocaleLowerCase("fr-FR");
    const bLast = (b.last_name || b.name || "").toLocaleLowerCase("fr-FR");
    if (aLast !== bLast) return aLast < bLast ? -1 : 1;
    const aFirst = (a.first_name || "").toLocaleLowerCase("fr-FR");
    const bFirst = (b.first_name || "").toLocaleLowerCase("fr-FR");
    return aFirst < bFirst ? -1 : aFirst > bFirst ? 1 : 0;
  });
}

/**
 * Return the candidate's display name as "LASTNAME Firstname" (FR admin convention).
 * Falls back to whatever fields are available.
 */
export function displayName(c) {
  if (!c) return "";
  const last = upperLastName(c.last_name || "");
  const first = (c.first_name || "").trim();
  if (last && first) return `${last} ${first}`;
  if (c.name) {
    // Try to detect "Firstname Lastname" and swap to "LASTNAME Firstname"
    const parts = c.name.trim().split(/\s+/);
    if (parts.length >= 2) {
      const inferredLast = upperLastName(parts[parts.length - 1]);
      const inferredFirst = parts.slice(0, -1).join(" ");
      return `${inferredLast} ${inferredFirst}`;
    }
    return c.name;
  }
  return [last, first].filter(Boolean).join(" ");
}
